const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function extractDateOnly(value: string): string | null {
  const iso = value.trim().slice(0, 10);
  return DATE_ONLY_RE.test(iso) ? iso : null;
}

/**
 * Parses a calendar date (YYYY-MM-DD) as local midnight.
 * Use for API date-only fields such as since, until, established_date, date_of_birth.
 */
export function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;

  const iso = extractDateOnly(value);
  if (!iso) return null;

  const [, y, m, d] = iso.match(DATE_ONLY_RE)!;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a calendar date for display in tables and cards.
 */
export function formatDateOnly(
  value?: string | null,
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string | null {
  if (!value) return null;

  const date = parseDateOnly(value);
  if (!date) return value.slice(0, 10);

  return date.toLocaleDateString(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    ...options,
  });
}

/**
 * Returns YYYY-MM-DD for HTML date input bindings.
 */
export function toDateOnlyInputValue(value?: string | null): string {
  if (!value) return "";

  const iso = extractDateOnly(value);
  return iso ?? value.slice(0, 10);
}

/**
 * Returns today's local calendar date as YYYY-MM-DD.
 */
export function todayDateOnly(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
